/**
 * Menu akcji „⋮" przy wierszu listy.
 * =================================
 *
 * Menu jedzie portalem do `document.body` i stoi na `position: fixed`, żeby nie
 * dało się go przyciąć kontenerem (`overflow-x-auto` opakowuje każdą tabelę).
 * Z tego wynika JEDEN niezmiennik, którego złamanie nie daje się zauważyć na
 * ekranie, na którym się pracuje:
 *
 *   POZYCJA JEST WE WSPÓŁRZĘDNYCH VIEWPORTU. Nigdy nie dodajemy `window.scrollY`
 *   ani `window.scrollX`.
 *
 * `getBoundingClientRect()` zwraca już współrzędne viewportu, a `fixed` je
 * przyjmuje — dodanie przewinięcia strony przesuwa menu o dokładnie tyle
 * pikseli, ile strona jest przewinięta. Przy `scrollY = 0` (lista mieszcząca się
 * bez przewijania) błąd wynosi zero i wszystko wygląda dobrze; im niżej na
 * stronie stoi lista, tym dalej POZA EKRAN ucieka menu. Tak było na „Moje
 * oferty" u dołu `/partner/pricing`: menu się otwierało, tylko ~1000 px pod
 * dolną krawędzią okna, więc kliknięcie ⋮ wyglądało na martwe (drugie kliknięcie
 * zamykało to, czego nie było widać).
 */

import { cn } from '@insolitum/ui';
import { MoreVertical } from 'lucide-react';
import { ReactNode, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/** Odstęp menu od przycisku i od krawędzi okna. */
const ODSTEP = 8;
/** `w-48` = 12rem — używane tylko, dopóki menu nie zostało zmierzone. */
const SZEROKOSC_MENU = 192;
/** Wysokość zapasowa na pierwszą klatkę, przed zmierzeniem menu. */
const WYSOKOSC_MENU = 200;

/** Trzyma współrzędną w oknie: `[ODSTEP, max - ODSTEP]`, z krawędzią jako ostatnią deską. */
function wOknie(wartosc: number, max: number): number {
  return Math.max(ODSTEP, Math.min(wartosc, max - ODSTEP));
}

interface ActionMenuProps {
  children: ReactNode;
  className?: string;
  buttonClassName?: string;
  menuClassName?: string;
  align?: 'left' | 'right';
  /** Accessible name for the icon-only trigger, e.g. `Akcje: ${org.name}` */
  label?: string;
}

interface MenuPosition {
  top: number;
  left: number;
}

export function ActionMenu({
  children,
  className,
  buttonClassName,
  menuClassName,
  align = 'right',
  label,
}: ActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  // `null` = jeszcze niezmierzone. Menu jest wtedy wyrenderowane (żeby dało się
  // je zmierzyć), ale niewidoczne — inaczej pierwsza klatka mrugałaby w (0, 0).
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || WYSOKOSC_MENU;
    const menuWidth = menuRef.current?.offsetWidth || SZEROKOSC_MENU;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // W górę tylko wtedy, gdy pod przyciskiem menu się nie mieści, a nad nim
    // miejsca jest więcej.
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < menuHeight + ODSTEP && spaceAbove > spaceBelow;

    // Bez `window.scrollY` / `window.scrollX` — patrz nagłówek pliku.
    const top = openUpward ? rect.top - menuHeight - ODSTEP : rect.bottom + ODSTEP;
    const left = align === 'right' ? rect.right - menuWidth : rect.left;

    setPosition({
      top: wOknie(top, viewportHeight - menuHeight),
      left: wOknie(left, viewportWidth - menuWidth),
    });
  }, [align]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    // Pomiar dopiero w następnej klatce — wcześniej menu nie jest w DOM-ie,
    // więc nie ma czego zmierzyć.
    requestAnimationFrame(() => {
      updatePosition();
    });
  }, [updatePosition]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Kolejne otwarcie dotyczy być może innego wiersza — stara pozycja
    // mrugnęłaby w złym miejscu na jedną klatkę.
    setPosition(null);
  }, []);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isOpen, handleOpen, handleClose]);

  // Handle clicks outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  return (
    <div className={cn('relative inline-block', className)}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={cn(
          'p-2 text-aurora-muted hover:text-aurora-text hover:bg-aurora-surface rounded-ids-md transition-colors',
          buttonClassName
        )}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={label ?? 'Więcej akcji'}
      >
        <MoreVertical className="w-5 h-5" />
      </button>

      {isOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-[9998]" onClick={handleClose} />
            {/* Menu */}
            <div
              ref={menuRef}
              role="menu"
              className={cn(
                'fixed z-[9999] w-48 bg-aurora-raised border border-aurora-border-strong rounded-ids-md shadow-elev-3 py-1',
                'animate-in fade-in-0 zoom-in-95 duration-100',
                menuClassName
              )}
              style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                visibility: position ? undefined : 'hidden',
              }}
            >
              {children}
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

interface ActionMenuItemProps {
  children: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'destructive' | 'primary';
  className?: string;
  disabled?: boolean;
}

export function ActionMenuItem({
  children,
  icon,
  onClick,
  variant = 'default',
  className,
  disabled,
}: ActionMenuItemProps) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
        {
          'text-aurora-muted hover:bg-aurora-surface hover:text-aurora-text':
            variant === 'default' && !disabled,
          'text-red-400 hover:bg-red-500/10': variant === 'destructive' && !disabled,
          'text-aurora-brand hover:bg-blue-500/10': variant === 'primary' && !disabled,
          'opacity-50 cursor-not-allowed': disabled,
        },
        className
      )}
    >
      {icon && <span className="w-4 h-4 flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
}

export function ActionMenuSeparator() {
  return <div role="separator" className="border-t border-aurora-border my-1" />;
}
